'use client'

import { useEffect, useRef } from 'react'

// QuantumLoader — écran de chargement HUD style « quantum dashboard »
// avec fond animé, anneaux superposés, particules et logo papillon au centre.
// Réutilisable (plein écran par défaut, ou inline via `fullscreen={false}`).

export function QuantumLoader({
  label = 'Initialisation…',
  fullscreen = true,
}: {
  label?: string
  fullscreen?: boolean
}) {
  const containerRef = useRef<HTMLDivElement>(null)

  // Injection des keyframes CSS pour les animations (une seule fois)
  useEffect(() => {
    const styleId = 'quantum-loader-styles'
    if (document.getElementById(styleId)) return

    const style = document.createElement('style')
    style.id = styleId
    style.textContent = `
      @keyframes hudSpin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes hudDraw {
        0% { stroke-dashoffset: 760; }
        50% { stroke-dashoffset: 0; }
        100% { stroke-dashoffset: -760; }
      }
      @keyframes hudPulse {
        0%, 100% { transform: scale(1); opacity: 0.8; }
        50% { transform: scale(1.08); opacity: 1; }
      }
      @keyframes shimmer {
        0% { opacity: 0.4; text-shadow: 0 0 8px rgba(79,124,255,0.2); }
        50% { opacity: 1; text-shadow: 0 0 20px rgba(79,124,255,0.8), 0 0 40px rgba(0,212,255,0.4); }
        100% { opacity: 0.4; text-shadow: 0 0 8px rgba(79,124,255,0.2); }
      }
      @keyframes orbit {
        from { transform: rotate(0deg) translateX(72px) rotate(0deg); }
        to { transform: rotate(360deg) translateX(72px) rotate(-360deg); }
      }
      @keyframes fieldWave {
        0% { transform: scaleX(0) scaleY(0); opacity: 0; }
        50% { transform: scaleX(1.2) scaleY(0.6); opacity: 0.6; }
        100% { transform: scaleX(0) scaleY(0); opacity: 0; }
      }
      @keyframes floatParticle {
        0% { transform: translate(0, 0) scale(1); opacity: 0.3; }
        50% { transform: translate(-20px, -30px) scale(1.6); opacity: 0.9; }
        100% { transform: translate(20px, 30px) scale(1); opacity: 0.3; }
      }
      @keyframes scanline {
        0% { transform: translateY(-100%); }
        100% { transform: translateY(100vh); }
      }
      .quantum-loader-bg {
        background: radial-gradient(circle at center, #0a0e1a 0%, #05080f 100%);
        position: relative;
        overflow: hidden;
      }
      .quantum-loader-bg::before {
        content: '';
        position: absolute;
        inset: 0;
        background-image: 
          linear-gradient(rgba(79,124,255,0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(79,124,255,0.03) 1px, transparent 1px);
        background-size: 30px 30px;
        pointer-events: none;
        z-index: 0;
      }
      .quantum-loader-bg::after {
        content: '';
        position: absolute;
        inset: 0;
        background: repeating-linear-gradient(
          0deg,
          transparent,
          transparent 4px,
          rgba(79,124,255,0.02) 4px,
          rgba(79,124,255,0.02) 5px
        );
        pointer-events: none;
        z-index: 0;
        animation: scanline 8s linear infinite;
      }
      .particle {
        position: absolute;
        border-radius: 50%;
        background: radial-gradient(circle, #4f7cff, transparent);
        pointer-events: none;
        z-index: 1;
        filter: blur(1px);
        animation: floatParticle 6s ease-in-out infinite alternate;
      }
    `
    document.head.appendChild(style)

    return () => {
      // Nettoyer le style si le composant est démonté (optionnel)
      // document.getElementById(styleId)?.remove()
    }
  }, [])

  return (
    <div
      ref={containerRef}
      className="quantum-loader-bg"
      style={{
        position: fullscreen ? 'fixed' : 'relative',
        inset: fullscreen ? 0 : undefined,
        zIndex: fullscreen ? 9999 : undefined,
        minHeight: fullscreen ? '100vh' : '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 28,
        padding: 24,
      }}
    >
      {/* Particules de fond (7 particules aléatoires) */}
      {Array.from({ length: 7 }).map((_, i) => {
        const size = 4 + Math.random() * 8
        const x = Math.random() * 100
        const y = Math.random() * 100
        const delay = Math.random() * 4
        const duration = 5 + Math.random() * 6
        return (
          <div
            key={i}
            className="particle"
            style={{
              width: size,
              height: size,
              left: `${x}%`,
              top: `${y}%`,
              animationDuration: `${duration}s`,
              animationDelay: `${delay}s`,
              opacity: 0.3 + Math.random() * 0.5,
            }}
          />
        )
      })}

      {/* Conteneur central */}
      <div
        style={{
          position: 'relative',
          width: 160,
          height: 160,
          display: 'grid',
          placeItems: 'center',
          zIndex: 2,
        }}
      >
        {/* Anneau extérieur (ligne de champ ondulée) */}
        <svg
          viewBox="0 0 200 200"
          width={160}
          height={160}
          style={{
            position: 'absolute',
            inset: 0,
            animation: 'hudSpin 12s linear infinite',
            opacity: 0.5,
          }}
        >
          <path
            d="M100 10 C 30 10, 10 50, 10 100 C 10 150, 30 190, 100 190 C 170 190, 190 150, 190 100 C 190 50, 170 10, 100 10"
            fill="none"
            stroke="rgba(79,124,255,0.15)"
            strokeWidth="1.5"
            strokeDasharray="8 6"
          />
        </svg>

        {/* Anneau spiralé principal (avec dessin) */}
        <svg
          viewBox="0 0 200 200"
          width={160}
          height={160}
          style={{
            position: 'absolute',
            inset: 0,
            animation: 'hudSpin 3.2s linear infinite',
          }}
        >
          <defs>
            <linearGradient id="qg" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#4f7cff" />
              <stop offset="100%" stopColor="#00d4ff" />
            </linearGradient>
            <filter id="glow">
              <feGaussianBlur stdDeviation="2" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <path
            d="M100 12 C 20 12, 20 188, 100 188 C 176 188, 176 20, 108 20 C 56 20, 44 64, 60 104 C 74 138, 118 138, 132 104"
            fill="none"
            stroke="url(#qg)"
            strokeWidth="2.8"
            strokeLinecap="round"
            filter="url(#glow)"
            style={{
              strokeDasharray: 760,
              strokeDashoffset: 760,
              animation: 'hudDraw 2.6s ease-in-out infinite alternate',
            }}
          />
        </svg>

        {/* Deuxième anneau intérieur (rotatif avec trait pointillé) */}
        <svg
          viewBox="0 0 200 200"
          width={120}
          height={120}
          style={{
            position: 'absolute',
            inset: 0,
            animation: 'hudSpin 6s linear infinite reverse',
            opacity: 0.6,
          }}
        >
          <circle
            cx="100"
            cy="100"
            r="40"
            fill="none"
            stroke="rgba(0,212,255,0.3)"
            strokeWidth="1.2"
            strokeDasharray="4 12"
          />
        </svg>

        {/* Lignes de champ ondulées (croisées) */}
        <svg
          viewBox="0 0 200 200"
          width={160}
          height={160}
          style={{
            position: 'absolute',
            inset: 0,
            animation: 'hudSpin 20s linear infinite',
            opacity: 0.3,
          }}
        >
          <path
            d="M20 100 Q 60 60, 100 100 T 180 100"
            fill="none"
            stroke="rgba(79,124,255,0.2)"
            strokeWidth="0.8"
          />
          <path
            d="M20 100 Q 60 140, 100 100 T 180 100"
            fill="none"
            stroke="rgba(0,212,255,0.2)"
            strokeWidth="0.8"
          />
        </svg>

        {/* Logo papillon au centre */}
        <img
          src="/logo.png"
          alt="Amiral Blanc"
          width={56}
          height={56}
          style={{
            objectFit: 'contain',
            filter: 'drop-shadow(0 0 20px rgba(79,124,255,0.8)) drop-shadow(0 0 40px rgba(0,212,255,0.4))',
            animation: 'hudPulse 2.4s ease-in-out infinite',
            zIndex: 3,
          }}
        />
      </div>

      {/* Label avec effet néon */}
      <p
        style={{
          fontFamily: 'var(--font-mono, monospace)',
          fontSize: 13,
          letterSpacing: '0.35em',
          textTransform: 'uppercase',
          color: '#b0c7ff',
          fontWeight: 300,
          animation: 'shimmer 1.8s ease-in-out infinite',
          textShadow: '0 0 30px rgba(79,124,255,0.3)',
          zIndex: 2,
          margin: 0,
          padding: '0 8px',
        }}
      >
        {label}
      </p>

      {/* Indicateur de progression (petite barre lumineuse) */}
      <div
        style={{
          width: 120,
          height: 2,
          background: 'rgba(79,124,255,0.2)',
          borderRadius: 4,
          overflow: 'hidden',
          zIndex: 2,
          marginTop: -8,
        }}
      >
        <div
          style={{
            width: '60%',
            height: '100%',
            background: 'linear-gradient(90deg, #4f7cff, #00d4ff)',
            borderRadius: 4,
            animation: 'shimmer 1.8s ease-in-out infinite',
            boxShadow: '0 0 20px rgba(79,124,255,0.6)',
          }}
        />
      </div>
    </div>
  )
}